import type { BaseQueryApi, BaseQueryFn } from "@reduxjs/toolkit/query";
import type {
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";
import Axios from "axios";
import { axiosPrivate } from "./axios";

export interface AxiosBaseQueryArgs<Meta> {
  meta?: Meta;
  prepareHeaders?: (
    headers: AxiosRequestHeaders,
    api: BaseQueryApi
  ) => AxiosRequestHeaders;
  transformResponse?: (response: Response) => unknown;
}

export interface ServiceExtraOptions {
  authRequired?: boolean;
}

type NewAxiosRequestConfig = AxiosRequestConfig;

const getRequestConfig = (args: string | AxiosRequestConfig) => {
  if (typeof args === "string") {
    return { url: args };
  }

  return args;
};

const axiosBaseQuery = <
  Args extends AxiosRequestConfig | string = AxiosRequestConfig,
  Result = unknown,
  DefinitionExtraOptions extends ServiceExtraOptions = Record<string, unknown>,
  Meta = Record<string, unknown>
>({
  prepareHeaders,
  meta,
  transformResponse,
}: AxiosBaseQueryArgs<Meta> = {}): BaseQueryFn<
  Args,
  Result,
  unknown,
  DefinitionExtraOptions,
  Meta
> => {
  return async (args, api, extraOptions) => {
    try {
      const requestConfig: NewAxiosRequestConfig = getRequestConfig(args);

      const result = await axiosPrivate({
        ...requestConfig,
        headers: prepareHeaders
          ? prepareHeaders(requestConfig.headers || ({} as any), api)
          : requestConfig.headers,
        signal: api.signal,
        withCredentials: false,
        ...extraOptions,
      });

      return {
        data: transformResponse ? transformResponse(result.data) : result.data,
        // The response headers, for the few endpoints whose answer does not fit
        // the body's shape — today only the availability search's category
        // outcome (`api/apartment/categoryOutcome.ts`). Axios lower-cases the
        // names. Read in an endpoint's `transformResponse(data, meta)`.
        meta: { ...meta, responseHeaders: result.headers } as Meta,
      };
    } catch (err: any) {

      if (!Axios.isAxiosError(err)) {
        return {
          error: err,
          meta,
        };
      } else {
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
          meta,
        };
      }
    }
  };
};

export default axiosBaseQuery;
